import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { MODEL_CONFIG, ModelMode, resolveModelMode } from "../config/modelConfig";
import { Workflow } from "../config/workflowConfig";
import { TroubleshootingOption } from "../config/troubleshootingConfig";
import { sendChatMessage } from "../services/api";
import { ChatImageRef, ChatMessage, Conversation, HistoryMessage } from "../types/chat";

interface SendParams {
  message: string;
  conversation: Conversation;
  modelMode: ModelMode;
  workflow: Workflow;
  troubleshootingOptions: TroubleshootingOption[];
  caseId?: string;
  images?: ChatImageRef[];
  onFallbackNotice?: (notice: string | null) => void;
  appendUserMessage: (user: ChatMessage) => void;
  appendMessages: (user: ChatMessage, assistant: ChatMessage) => void;
}

export function useChatSend() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (params: SendParams) => {
    const {
      message,
      conversation,
      modelMode,
      workflow,
      troubleshootingOptions,
      caseId,
      images,
      onFallbackNotice,
      appendUserMessage,
      appendMessages,
    } = params;

    const { effective, notice: clientNotice } = resolveModelMode(modelMode);
    if (clientNotice) onFallbackNotice?.(clientNotice);

    const userMsg: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: message,
      images: images && images.length > 0 ? images : undefined,
      timestamp: new Date(),
    };

    appendUserMessage(userMsg);
    setIsLoading(true);
    setError(null);

    try {
      const history: HistoryMessage[] = conversation.messages.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
      }));

      const res = await sendChatMessage({
        message,
        conversation_id: conversation.conversationId,
        customer_name: conversation.customerName,
        case_id: caseId,
        workflow,
        model_mode: effective,
        model: MODEL_CONFIG[effective].backendModel,
        troubleshooting_options: troubleshootingOptions,
        history,
        images,
      });

      if (res.model_fallback_notice) {
        onFallbackNotice?.(res.model_fallback_notice);
      }

      const assistantMsg: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: res.response,
        sources: res.sources,
        kb_status: res.kb_status,
        confidence_note: res.confidence_note,
        escalation_suggested: res.escalation_suggested,
        intent: res.intent,
        session_id: res.session_id || conversation.conversationId,
        timestamp: new Date(),
      };

      appendMessages(userMsg, assistantMsg);
    } catch (err: unknown) {
      let msg = "Request failed. Is the backend running?";
      if (err && typeof err === "object" && "response" in err) {
        const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } }).response;
        const detail = resp?.data?.detail;
        if (typeof detail === "string" && detail.length > 0) {
          msg = detail;
        } else if (resp?.status) {
          msg = `Server error (${resp.status})`;
        }
      } else if (
        err &&
        typeof err === "object" &&
        (err as { code?: string }).code === "ECONNABORTED"
      ) {
        msg =
          "The knowledge base search took too long. Try again, or narrow the question to one product and symptom.";
      } else if (err instanceof Error && err.message) {
        msg = err.message === "Network Error"
          ? "Network error — backend unreachable or blocked by proxy/SSL."
          : err.message;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, setError, sendMessage };
}
